import { memo, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNil, isString } from 'lodash';
// Import Store
import { useAppDispatch, useAppSelector } from 'store';
import {
    resetRouteLeaveHasDataChange,
    selectRouteLeaveConfirmation,
    setRouteLeavePageName,
} from 'store/slices/routeLeaveConfirmation';
// Import Constants
import { GlobalEvents, l, ModalsClassNames, RouteLeaveConfirmationType, NavigationActionTypes } from 'constants/common';
// Import Components
import ConfirmationModal from 'components/common/Modals/ConfirmationModal';
// Import Hooks
import { useUserConfirmation } from 'hooks/UseUserConfirmation';
// Import Services
import { publish } from 'services/event';

const RouteLeaveConfirmation = ({ routeLeaveConfirmationType, pageName }) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { getConfirmationCallback, setConfirmationCallback } = useUserConfirmation();

    const { hasDataChange, isChangeSaved } = useAppSelector(selectRouteLeaveConfirmation);

    const [isConfirmationModalVisible, setIsConfirmationModalVisible] = useState(false);

    const shouldConfirm = useMemo(() => {
        if (isChangeSaved) return false;
        if (routeLeaveConfirmationType === RouteLeaveConfirmationType.Always) return true;
        if (routeLeaveConfirmationType === RouteLeaveConfirmationType.ChangeRelated) return !!hasDataChange;
        return false;
    }, [routeLeaveConfirmationType, hasDataChange, isChangeSaved]);

    const navigationActionTypeRef = useRef(NavigationActionTypes.PUSH);
    const isNavigationAcceptedRef = useRef(false);
    const nextLocationRef = useRef(null);

    const init = () => {
        dispatch(setRouteLeavePageName(pageName));
    };

    const cleanUp = () => {
        return () => {
            dispatch(resetRouteLeaveHasDataChange());
        };
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(init, []);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(cleanUp, []);

    const showModal = (location, actionType) => {
        const tmpActionType =
            isNil(actionType) && isString(actionType) ? NavigationActionTypes.PUSH : actionType.toUpperCase();
        navigationActionTypeRef.current = isNil(NavigationActionTypes[tmpActionType])
            ? NavigationActionTypes.PUSH
            : NavigationActionTypes[tmpActionType];

        setIsConfirmationModalVisible(true);
        nextLocationRef.current = location;
    };

    const navigationBlockHandler = (nextLocation, actionType) => {
        if (!isNavigationAcceptedRef.current) {
            showModal(nextLocation, actionType);
            // return empty string for calling BrowserRouter getUserConfirmation
            return '';
        }
        isNavigationAcceptedRef.current = false;
        return true;
    };

    const acceptNavigation = () => {
        dispatch(resetRouteLeaveHasDataChange());
        publish(GlobalEvents.RouteLeaveConfirmed);
        isNavigationAcceptedRef.current = true;
        navigation(true);
    };

    const navigation = (isAccept) => {
        setIsConfirmationModalVisible(false);
        const callback = getConfirmationCallback();
        setConfirmationCallback(null);
        callback(isAccept);
    };

    return (
        <>
            <Prompt when={shouldConfirm} message={navigationBlockHandler} />
            <ConfirmationModal
                isVisibleModal={isConfirmationModalVisible}
                onOk={() => navigation(false)}
                onCancel={acceptNavigation}
                titleText={t(l.LeavePage)}
                questionLabel={t(l.YouHaveUnsavedChangesAreYouSureYouWantToLeavePage, { pageName: t(pageName) })}
                actionLabel={l.Stay}
                cancelLabel={l.Leave}
                className={ModalsClassNames.Warning}
                iconType="bc-icon-danger-48"
                isLoading={false}
            />
        </>
    );
};

RouteLeaveConfirmation.propTypes = {
    routeLeaveConfirmationType: PropTypes.number.isRequired,
    pageName: PropTypes.string.isRequired,
};

export default memo(RouteLeaveConfirmation);
